/**
 * Seed Data Utility
 *
 * Seeds all listings from gobnb.net database to Firestore.
 * Call window.seedListings() in browser console to populate Firestore.
 */

import { collection, doc, setDoc, getDocs, query, limit, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { ALL_LISTINGS } from './allListings';

export async function clearListings() {
  console.log('🗑️ Clearing existing listings...');
  const snapshot = await getDocs(collection(db, 'listings'));
  for (const docSnap of snapshot.docs) {
    await deleteDoc(doc(db, 'listings', docSnap.id));
  }
  console.log(`✅ Cleared ${snapshot.size} listings`);
}

/**
 * Convert email to the sanitized ID format used as hostId
 * e.g. "gobnbnet@gmail.com" -> "gobnbnet_gmail_com"
 */
function emailToHostId(email: string): string {
  return email.toLowerCase().replace(/[^a-z0-9]/g, '_');
}

export async function seedListings() {
  console.log('🌱 Seeding all listings to Firestore...');
  console.log(`📊 Total listings: ${ALL_LISTINGS.length}`);

  let success = 0;
  let failed = 0;

  for (const listing of ALL_LISTINGS) {
    try {
      // Derive hostId from host.email for consistent host-to-listing mapping
      const hostEmail = listing.host?.email || '';
      const hostId = hostEmail ? emailToHostId(hostEmail) : listing.hostId;

      const docRef = doc(db, 'listings', listing.id);
      await setDoc(docRef, {
        ...listing,
        hostId,
        host: {
          ...listing.host,
          id: hostId,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        views: Math.floor(Math.random() * 500) + 100,
        bookingCount: Math.floor(Math.random() * 50) + 10,
      });
      success++;
      if (success % 10 === 0) {
        console.log(`✅ Seeded ${success}/${ALL_LISTINGS.length} listings...`);
      }
    } catch (error) {
      console.error(`❌ Error adding ${listing.title}:`, error);
      failed++;
    }
  }

  console.log(`🎉 Seeding complete! ${success} succeeded, ${failed} failed`);
  return { success, failed };
}

export async function reseedListings() {
  await clearListings();
  await seedListings();
  console.log('🔄 Refreshing page...');
  window.location.reload();
}

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  const win = window as unknown as {
    seedListings: typeof seedListings;
    clearListings: typeof clearListings;
    reseedListings: typeof reseedListings;
  };
  win.seedListings = seedListings;
  win.clearListings = clearListings;
  win.reseedListings = reseedListings;

  // Check URL for reseed trigger
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('reseed') === 'true') {
    console.log('🔄 Reseed triggered via URL parameter...');
    (async () => {
      await clearListings();
      await seedListings();
      // Remove the parameter and reload
      window.history.replaceState({}, '', window.location.pathname);
      window.location.reload();
    })();
  } else {
    // Auto-seed on page load if Firestore is empty
    (async () => {
      try {
        const snapshot = await getDocs(query(collection(db, 'listings'), limit(1)));
        if (snapshot.empty) {
          console.log('🌱 No listings found, auto-seeding...');
          await seedListings();
          window.location.reload();
        }
      } catch (error) {
        console.error('Error checking Firestore:', error);
      }
    })();
  }
}

export default ALL_LISTINGS;
